<template>
    <div>
        <div class="filters-container">
            <div class="row g-3 align-items-end">
                <div class="col-lg-4 col-md-6">
                    <label for="resumenInmobiliaria" class="form-label fw-semibold">
                        <i class="fas fa-building text-secondary me-1"></i>
                        Inmobiliaria
                    </label>
                    <select
                        id="resumenInmobiliaria"
                        class="form-select form-select-sm"
                        :value="filtros.inmobiliaria_id ?? ''"
                        @change="actualizar('inmobiliaria_id', $event.target.value)"
                    >
                        <option value="">Todas</option>
                        <option v-for="inmo in inmobiliarias" :key="inmo.id" :value="inmo.id">
                            {{ inmo.nombre }} ({{ inmo.negocios }})
                        </option>
                    </select>
                </div>

                <div class="col-lg-3 col-md-6">
                    <label for="resumenTipo" class="form-label fw-semibold">Tipo</label>
                    <select
                        id="resumenTipo"
                        class="form-select form-select-sm"
                        :value="filtros.tipo ?? ''"
                        @change="actualizar('tipo', $event.target.value)"
                    >
                        <option value="">Todos</option>
                        <option v-for="tipo in tipos" :key="tipo.value" :value="tipo.value">{{ tipo.label }}</option>
                    </select>
                </div>

                <div class="col-lg-2 col-md-6">
                    <label for="resumenDesde" class="form-label fw-semibold">
                        <i class="fas fa-calendar-alt text-secondary me-1"></i>
                        Desde
                    </label>
                    <input
                        id="resumenDesde"
                        type="date"
                        class="form-control form-control-sm"
                        :value="filtros.fecha_desde"
                        @change="actualizar('fecha_desde', $event.target.value)"
                    />
                </div>

                <div class="col-lg-2 col-md-6">
                    <label for="resumenHasta" class="form-label fw-semibold">Hasta</label>
                    <input
                        id="resumenHasta"
                        type="date"
                        class="form-control form-control-sm"
                        :value="filtros.fecha_hasta"
                        @change="actualizar('fecha_hasta', $event.target.value)"
                    />
                </div>
            </div>

            <div class="actions-row">
                <div class="d-flex gap-2 flex-wrap align-items-center">
                    <SoftButton color="primary" size="sm" variant="gradient" @click="$emit('search')">
                        <i class="fas fa-search me-1"></i>
                        Filtrar
                    </SoftButton>
                    <SoftButton color="secondary" size="sm" variant="outline" @click="$emit('clear')">
                        <i class="fas fa-broom me-1"></i>
                        Limpiar
                    </SoftButton>
                    <SoftButton color="secondary" size="sm" variant="outline" @click="$emit('refresh')">
                        <i class="fas fa-sync-alt me-1"></i>
                        Actualizar
                    </SoftButton>
                </div>
                <p class="text-xxs text-muted mb-0 mt-2">
                    <i class="fas fa-info-circle me-1"></i>
                    Las fechas filtran por la fecha de cierre (o la de creación si el borrador aún no la tiene).
                    Los montos suman solo transacciones confirmadas, sin convertir moneda.
                </p>
            </div>
        </div>

        <soft-spinner v-if="cargando" />

        <template v-else-if="resumen">
            <!-- Totales -->
            <div class="row g-3 mb-4">
                <div class="col-xl-3 col-sm-6">
                    <div class="stat-card d-flex align-items-center gap-3">
                        <div class="stat-card__icon bg-gradient-primary">
                            <i class="fas fa-handshake text-white"></i>
                        </div>
                        <div>
                            <p class="stat-card__label">Transacciones</p>
                            <p class="stat-card__value">{{ formatNumero(totales.total) }}</p>
                            <p class="stat-card__hint">{{ formatNumero(totales.inmobiliarias) }} inmobiliarias</p>
                        </div>
                    </div>
                </div>
                <div class="col-xl-3 col-sm-6">
                    <div class="stat-card d-flex align-items-center gap-3">
                        <div class="stat-card__icon bg-gradient-success">
                            <i class="fas fa-check text-white"></i>
                        </div>
                        <div>
                            <p class="stat-card__label">Confirmadas</p>
                            <p class="stat-card__value">{{ formatNumero(totales.confirmadas) }}</p>
                            <p class="stat-card__hint">{{ porcentajeConfirmadas }}</p>
                        </div>
                    </div>
                </div>
                <div class="col-xl-3 col-sm-6">
                    <div class="stat-card d-flex align-items-center gap-3">
                        <div class="stat-card__icon bg-gradient-info">
                            <i class="fas fa-coins text-white"></i>
                        </div>
                        <div class="min-w-0">
                            <p class="stat-card__label">Valor confirmado</p>
                            <p class="stat-card__value stat-card__value--monto">{{ formatMonto(totales.valor_confirmado) }}</p>
                            <p class="stat-card__hint">Precios de venta y cánones</p>
                        </div>
                    </div>
                </div>
                <div class="col-xl-3 col-sm-6">
                    <div class="stat-card d-flex align-items-center gap-3">
                        <div class="stat-card__icon bg-gradient-warning">
                            <i class="fas fa-percentage text-white"></i>
                        </div>
                        <div class="min-w-0">
                            <p class="stat-card__label">Comisión confirmada</p>
                            <p class="stat-card__value stat-card__value--monto">{{ formatMonto(totales.comision_confirmada) }}</p>
                            <p class="stat-card__hint">Con IVA</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Por tipo -->
            <div class="row g-3 mb-4">
                <div v-for="(datos, tipo) in totales.por_tipo || {}" :key="tipo" class="col-lg-4">
                    <div class="tipo-card h-100" :class="claseTipo(tipo)">
                        <h6 class="tipo-card__titulo">{{ datos.nombre }}</h6>
                        <div class="d-flex gap-2 flex-wrap mb-3">
                            <span class="badge bg-success">{{ datos.confirmada }} confirmadas</span>
                            <span class="badge bg-secondary">{{ datos.borrador }} borradores</span>
                            <span class="badge bg-danger">{{ datos.cancelada }} canceladas</span>
                        </div>
                        <dl class="tipo-card__montos mb-0">
                            <dt>Valor confirmado</dt>
                            <dd>{{ formatMonto(datos.valor_confirmado) }}</dd>
                            <dt>{{ tipo === 'alquiler_administracion' ? 'Comisión de colocación' : 'Comisión' }}</dt>
                            <dd>{{ formatMonto(datos.comision_confirmada) }}</dd>
                            <template v-if="datos.honorarios_mensuales_confirmados !== undefined">
                                <dt>Honorarios mensuales</dt>
                                <dd>{{ formatMonto(datos.honorarios_mensuales_confirmados) }} <span class="text-muted">antes de IVA</span></dd>
                            </template>
                        </dl>
                    </div>
                </div>
            </div>

            <!-- Por inmobiliaria -->
            <div class="crm-card">
                <div class="crm-card__header d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <h6 class="mb-0 fw-bold">Por inmobiliaria</h6>
                    <span class="text-xxs text-muted">Ordenado por transacciones confirmadas</span>
                </div>
                <div class="table-responsive">
                    <table class="table table-hover mb-0">
                        <thead>
                            <tr>
                                <th>Inmobiliaria</th>
                                <th class="text-end">Total</th>
                                <th class="text-end">Confirmadas</th>
                                <th>Por tipo (confirmadas)</th>
                                <th class="text-end">Valor confirmado</th>
                                <th class="text-end">Comisión confirmada</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="fila in porInmobiliaria" :key="fila.inmobiliaria.id">
                                <td>
                                    <span class="d-block text-sm fw-semibold text-dark">{{ fila.inmobiliaria.nombre }}</span>
                                    <span class="d-block text-xxs text-muted">{{ fila.inmobiliaria.email }}</span>
                                </td>
                                <td class="text-end">{{ formatNumero(fila.total) }}</td>
                                <td class="text-end fw-semibold">{{ formatNumero(fila.confirmadas) }}</td>
                                <td>
                                    <span
                                        v-for="(datos, tipo) in fila.por_tipo || {}"
                                        :key="tipo"
                                        class="badge tipo-badge me-1"
                                        :class="claseTipo(tipo)"
                                        :title="datos.nombre"
                                    >
                                        {{ siglaTipo(tipo) }} {{ datos.confirmada }}
                                    </span>
                                </td>
                                <td class="text-end text-nowrap">{{ formatMonto(fila.valor_confirmado) }}</td>
                                <td class="text-end text-nowrap">{{ formatMonto(fila.comision_confirmada) }}</td>
                                <td class="text-end">
                                    <a
                                        href="#"
                                        class="text-xs fw-semibold text-nowrap"
                                        @click.prevent="$emit('ver-transacciones', fila.inmobiliaria.id)"
                                    >
                                        Ver transacciones <i class="fas fa-arrow-right ms-1"></i>
                                    </a>
                                </td>
                            </tr>
                            <tr v-if="!porInmobiliaria.length">
                                <td colspan="7" class="text-center text-muted py-4">
                                    <i class="fas fa-inbox mb-2 d-block" style="font-size:1.5rem; color:#CBD5E1"></i>
                                    No hay transacciones con estos filtros
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import SoftButton from '@/components/SoftButton.vue';
import SoftSpinner from '@/components/SoftSpinner.vue';
import { TIPOS, formatMonto, formatNumero, claseTipo } from '../formatoNegocios.js';

const SIGLAS = { venta: 'V', alquiler_administracion: 'AA', alquiler_corretaje: 'AC' };

export default {
    name: 'ReporteNegocios',
    components: { SoftButton, SoftSpinner },
    props: {
        filtros: { type: Object, required: true },
        inmobiliarias: { type: Array, default: () => [] },
        resumen: { type: Object, default: null },
        cargando: { type: Boolean, default: false }
    },
    emits: ['update:filtros', 'search', 'clear', 'refresh', 'ver-transacciones'],
    setup() {
        return { tipos: TIPOS, formatMonto, formatNumero, claseTipo };
    },
    computed: {
        totales() {
            return this.resumen?.totales || {};
        },
        porInmobiliaria() {
            return this.resumen?.por_inmobiliaria || [];
        },
        porcentajeConfirmadas() {
            if (!this.totales.total) return 'Sin transacciones';
            return `${Math.round((this.totales.confirmadas / this.totales.total) * 100)} % del total`;
        }
    },
    methods: {
        actualizar(clave, valor) {
            this.$emit('update:filtros', { ...this.filtros, [clave]: valor === '' ? null : valor });
        },
        siglaTipo(tipo) {
            return SIGLAS[tipo] || tipo;
        }
    }
};
</script>

<style lang="scss" scoped>
.filters-container {
    background: #f8f9fa;
    border-radius: 0.75rem;
    padding: 1.25rem;
    margin-bottom: 1.5rem;
    border: 1px solid #e9ecef;

    .form-label {
        margin-bottom: 0.4rem;
        color: #344767;
        font-size: 0.8rem;
    }
}

.actions-row {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
}

.min-w-0 {
    min-width: 0;
}

.stat-card__value--monto {
    font-size: 1.15rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.stat-card__hint {
    font-size: 0.68rem;
    color: #94A3B8;
    margin: 4px 0 0;
}

.tipo-card {
    background: #fff;
    border: 1px solid #E2E8F0;
    border-left: 4px solid var(--tipo-color);
    border-radius: 12px;
    padding: 16px 18px;

    &__titulo {
        font-size: 0.85rem;
        font-weight: 700;
        color: #1E293B;
        margin-bottom: 10px;
    }

    &__montos {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 4px 12px;
        font-size: 0.8rem;

        dt {
            color: #64748B;
            font-weight: 600;
        }

        dd {
            margin: 0;
            text-align: right;
            color: #1E293B;
            font-weight: 600;
        }
    }
}

.tipo-venta { --tipo-color: #2563EB; --tipo-fondo: #EFF6FF; }
.tipo-administracion { --tipo-color: #059669; --tipo-fondo: #ECFDF5; }
.tipo-corretaje { --tipo-color: #D97706; --tipo-fondo: #FFFBEB; }

.tipo-badge {
    background: var(--tipo-fondo);
    color: var(--tipo-color);
    border: 1px solid var(--tipo-color);
}

.crm-card {
    background: #fff;
    border: 1px solid #E2E8F0;
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.05);
    overflow: hidden;
}

.crm-card__header {
    padding: 16px 20px;
    border-bottom: 1px solid #F1F5F9;
}
</style>
